import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import {
  Mail, MapPin, MessageSquare, Send,
  Building2, HelpCircle, FileText, Shield,
} from 'lucide-react';

// Contact — Swiss Minimalist + Pastel theme.
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9
// Fonts: .font-display (Playfair Display) · .font-body (Manrope) · .font-label (Outfit)

const Contact = () => {
  const navigate = useNavigate();

  const contactTopics = [
    { icon: MessageSquare, tint: '#D4E0DA', title: 'General Inquiries',     description: 'Questions about our services, pricing, or how Nova can help your business.' },
    { icon: FileText,      tint: '#EBD8C8', title: 'Sales & Partnerships',  description: 'Interested in enterprise plans or partnership opportunities.' },
    { icon: HelpCircle,    tint: '#D9CFD9', title: 'Customer Support',      description: 'Technical support and assistance for existing customers.' },
    { icon: Shield,        tint: '#D4E0DA', title: 'Compliance Questions',  description: 'Questions about SOC 2, GDPR, HIPAA, ISO 27001, and other frameworks.' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]"
        data-testid="contact-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
            Get In Touch
          </p>
          <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-8">
            We're here to help
            <br />
            let's start a conversation
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-2xl mx-auto leading-relaxed">
            Have questions about compliance and certification, or want to see how Nova fits your business? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* ===================== PRIMARY CONTACT (Two Pillars) ===================== */}
      <section className="px-6 py-24 md:py-28" data-testid="contact-primary">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
            {/* Email */}
            <div className="rounded-2xl bg-[#D4E0DA]/45 dark:bg-[#101F18]/45 px-8 py-10 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#D4E0DA] dark:bg-[#101F18] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <p className="font-label text-[11px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55">
                  Email Us
                </p>
              </div>
              <a
                href="mailto:contact@teamnova.ai"
                data-testid="contact-email-primary"
                className="font-display text-2xl md:text-3xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight block mb-3 hover:underline underline-offset-4 decoration-[#EBD8C8] decoration-2"
              >
                contact@teamnova.ai
              </a>
              <p className="text-sm text-black/65 dark:text-white/65">
                Typical response — within 24 hours
              </p>
              <div className="mt-8 pt-6 border-t border-black/10 dark:border-white/10 space-y-2 text-sm">
                <p className="text-black/70 dark:text-white/70">
                  <span className="font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">Privacy &amp; Legal — </span>
                  <a
                    href="mailto:privacy@teamnova.ai"
                    className="hover:underline underline-offset-4 decoration-[#EBD8C8] decoration-2"
                  >
                    privacy@teamnova.ai
                  </a>
                </p>
                <p className="text-black/70 dark:text-white/70">
                  <span className="font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">Response time — </span>
                  12–24 hours
                </p>
              </div>
            </div>

            {/* Address */}
            <div className="rounded-2xl bg-[#EBD8C8]/45 dark:bg-[#2E1B10]/45 px-8 py-10 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#EBD8C8] dark:bg-[#2E1B10] flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <p className="font-label text-[11px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55">
                  Office Address
                </p>
              </div>
              <p className="font-display text-xl md:text-2xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight mb-3">
                Inceptica LLC <span className="italic text-black/65 dark:text-white/65">(Nova)</span>
              </p>
              <address className="not-italic text-base text-black/75 dark:text-white/75 leading-relaxed">
                2389 Main St. STE 100<br />
                Glastonbury, CT 06033<br />
                United States
              </address>
              <div className="mt-8 pt-6 border-t border-black/10 dark:border-white/10 text-sm">
                <p className="text-black/70 dark:text-white/70">
                  <span className="font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">Hours — </span>
                  Monday to Friday, 9am – 6pm ET
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TOPICS BENTO ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="contact-topics">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              How We Can Help
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4">
              What can we help you with?
            </h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto">
              Pick the topic closest to your question — we'll route you to the right team.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-black/10 dark:border-white/10">
            {contactTopics.map((topic, i) => {
              const Icon = topic.icon;
              return (
                <a
                  key={topic.title}
                  href="mailto:contact@teamnova.ai"
                  data-testid={`contact-topic-${i}`}
                  className="group block border-r border-b border-black/10 dark:border-white/10 p-8 md:p-10 transition-colors duration-700"
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = topic.tint + '40'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-8 nv-tint"
                    style={{ backgroundColor: topic.tint + 'CC' }}
                  >
                    <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                  </div>
                  <p
                    className="font-display font-semibold leading-none text-5xl md:text-6xl mb-5 select-none"
                    style={{ color: topic.tint }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="font-display text-xl md:text-2xl leading-tight mb-2">
                    {topic.title}
                  </h3>
                  <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">
                    {topic.description}
                  </p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== QUICK LINKS ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="contact-quicklinks">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Free Trial */}
          <div className="rounded-2xl bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] px-8 py-10 md:p-12 flex flex-col">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-white/55 mb-3">
              Begin
            </p>
            <h3 className="font-display text-2xl md:text-3xl mb-4 leading-tight">
              Ready to get started?
            </h3>
            <p className="text-sm text-white/70 mb-8 leading-relaxed flex-1">
              Try Nova free for 30 days — no commitments, no credit card required.
            </p>
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="contact-start-free"
              className="font-label uppercase tracking-[0.2em] text-[11px] bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] py-3.5 px-5 rounded-full shadow-sm hover:shadow-md transition-all duration-500 inline-flex items-center justify-center gap-2 whitespace-nowrap self-start"
            >
              Start Free Trial
              <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>

          {/* Learn More */}
          <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#050505] px-8 py-10 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#D9CFD9] dark:bg-[#1E1724] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl md:text-2xl leading-tight">
                Learn more about Nova
              </h3>
            </div>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/about')}
                data-testid="contact-link-about"
                className="w-full text-left px-4 py-3.5 border border-black/10 dark:border-white/10 rounded-xl hover:bg-[#D4E0DA]/25 dark:hover:bg-[#101F18]/25 hover:border-black/20 dark:hover:border-white/20 transition-colors duration-500"
              >
                <div className="font-display text-base text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight">About Us</div>
                <div className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mt-1">Our mission and team</div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                data-testid="contact-link-pricing"
                className="w-full text-left px-4 py-3.5 border border-black/10 dark:border-white/10 rounded-xl hover:bg-[#EBD8C8]/25 dark:hover:bg-[#2E1B10]/25 hover:border-black/20 dark:hover:border-white/20 transition-colors duration-500"
              >
                <div className="font-display text-base text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight">Pricing</div>
                <div className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mt-1">Transparent pricing plans</div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/features')}
                data-testid="contact-link-features"
                className="w-full text-left px-4 py-3.5 border border-black/10 dark:border-white/10 rounded-xl hover:bg-[#D9CFD9]/25 dark:hover:bg-[#1E1724]/25 hover:border-black/20 dark:hover:border-white/20 transition-colors duration-500"
              >
                <div className="font-display text-base text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight">Features</div>
                <div className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mt-1">What Nova does for you</div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section
        className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center"
        data-testid="contact-cta-section"
      >
        <div className="max-w-4xl mx-auto">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-white/65 mb-6">
            Begin
          </p>
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-6 leading-[1.1] tracking-tight">
            Let's start a conversation.
          </h2>
          <p className="font-body text-sm md:text-base text-white/70 mb-12 max-w-2xl mx-auto">
            Talk to our team about your compliance goals and we'll help you prepare for certification with trusted independent auditors.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:contact@teamnova.ai" data-testid="contact-cta-email">
              <button
                type="button"
                className="font-label bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-[#D4E0DA] dark:hover:bg-[#101F18] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500 inline-flex items-center gap-2"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                Send Us an Email
              </button>
            </a>
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="contact-cta-start"
              className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full shadow-sm hover:shadow-md transition-all duration-500"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
